import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "./UiDb";
import {
  NineSetup,
  CalendarData,
} from "../../interfaces/NineSetupObject";
import { resetAllState } from "./resetAllState";
import { registerAsync } from "./shared/registerAsync";
import {
  createNineSetup,
  deleteNineSetup,
  fetchOperatorQueue,
  fetchCuratorQueue,
  fetchApprovedQueue,
  fetchCuratorSetups,
  fetchNineSetups,
  fetchTeamSetups,
  updateNineSetup,
  fetchCalendarData,
  fetchReportRange,
} from "../../services/thunks/NineSetupThunk";
import { updateBusiness } from "../../services/thunks/BusinessThunk";

/**
 * State for the Nine Setup pipeline (table `nine_setup`) — a captain's
 * prospects from first outreach through curation to a live business page.
 *
 * Kept separate from OnboardingDb even though both are captain-facing: they
 * model different backend tables with different lifecycles, and OnboardingDb's
 * state is shaped around a captain's own dashboard rather than a shared funnel.
 */
interface NineSetupState {
  /** All rows the caller can see, newest first. Filtering by status is done in
   *  the component (or server-side via fetch filters) — this list is never
   *  pre-filtered, so a status change never has to trigger a refetch. */
  setups: NineSetup[];
  /**
   * The shared operator queue — approved rows waiting for media collection.
   */
  operatorQueue: NineSetup[];
  /**
   * The shared curation queue — media_collected rows waiting to be claimed by curators.
   *
   * A SEPARATE list, not a filtered view of `setups`: it's a different scope
   * (other people's rows) from a different endpoint. Merging the two would make
   * the captain's own list silently grow when a curator opened the queue.
   */
  queue: NineSetup[];
  /** Rows this curator has claimed — their own workload. Also a distinct scope. */
  claimed: NineSetup[];
  /** A lead's team's rows — another distinct scope, read-only (earnings). */
  teamSetups: NineSetup[];
  /** Calendar summary data - distinct scope from the lists above */
  calendarData: CalendarData | null;
  calendarDataRequestId: string | null;
  /** Report ranges (all statuses) for the Onboarding Insights views, keyed by
   *  reportRangeKey(from, to). Separate from calendarData on purpose so a
   *  targets fetch never replaces the calendar's month. */
  reportRanges: Record<string, CalendarData>;
  /** The row a detail panel/modal is focused on. Holds an id rather than the
   *  row itself so it can't go stale when the list is refetched. */
  selectedSetupId: string | null;
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: NineSetupState = {
  setups: [],
  operatorQueue: [],
  queue: [],
  claimed: [],
  teamSetups: [],
  calendarData: null,
  calendarDataRequestId: null,
  reportRanges: {},
  selectedSetupId: null,
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

/**
 * Replace the row with the same setupId, or prepend if it's new.
 *
 * Used by both create and update so neither can duplicate a card: an update
 * must land in place (preserving list order), while a create belongs at the top
 * of a newest-first list. Prepending also matters for the retry-after-failure
 * case, where a create can return a row the client already has.
 *
 * In-place mutation by design: only ever called on Immer drafts inside this
 * slice's reducers (state.setups is always initialized to []).
 */
const upsertNineSetup = (list: NineSetup[], row: NineSetup): void => {
  const existingIdx = list.findIndex((s) => s.setupId === row.setupId);
  if (existingIdx >= 0) {
    list[existingIdx] = row;
  } else {
    list.unshift(row);
  }
};

/** Per-row loadingMap/errorMap key. Update and delete are row-scoped actions —
 *  a single global key would disable every row's controls while one row saves.
 *  Components must read the exact same key to guard against double-submit. */
export const updateNineSetupKey = (setupId: string): string =>
  `updateNineSetup_${setupId}`;
export const deleteNineSetupKey = (setupId: string): string =>
  `deleteNineSetup_${setupId}`;
export const fetchCalendarDataKey = "fetchCalendarData";

// Selectors for Calendar Data
export const selectCalendarData = (state: RootState) => state.nineSetup.calendarData;
export const selectCalendarLoading = (state: RootState) => state.nineSetup.loadingMap[fetchCalendarDataKey];
export const selectCalendarError = (state: RootState) => state.nineSetup.errorMap[fetchCalendarDataKey];

/** Cache key for one report range, and its loadingMap/errorMap key. */
export const reportRangeKey = (from: string, to: string): string => `${from}|${to}`;
export const fetchReportRangeKey = (rangeKey: string): string =>
  `fetchReportRange_${rangeKey}`;

export const selectReportRange = (state: RootState, from: string, to: string) =>
  state.nineSetup.reportRanges[reportRangeKey(from, to)];
export const selectReportRangeLoading = (state: RootState, from: string, to: string) =>
  state.nineSetup.loadingMap[fetchReportRangeKey(reportRangeKey(from, to))];
export const selectReportRangeError = (state: RootState, from: string, to: string) =>
  state.nineSetup.errorMap[fetchReportRangeKey(reportRangeKey(from, to))] ?? null;

const nineSetupSlice = createSlice({
  name: "nineSetup",
  initialState,
  reducers: {
    /** Focus a row for a detail view. Pass null to close it. */
    selectNineSetup: (state, action: PayloadAction<string | null>) => {
      state.selectedSetupId = action.payload;
    },
    /**
     * Clears the error/success flags for one action key after the UI has shown
     * them. Without this a toast re-fires on every re-render that reads the
     * map, and a stale success flag makes the next open of a form look like it
     * already succeeded. Accepts the same keys the thunks write, including the
     * per-row ones built above.
     */
    clearNineSetupFeedback: (state, action: PayloadAction<string>) => {
      state.errorMap[action.payload] = null;
      state.successMap[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    // fetchNineSetups — replaces the list wholesale (the server response is
    // authoritative; merging would strand rows deleted by someone else).
    builder
      .addCase(fetchNineSetups.pending, (state) => {
        state.loadingMap.fetchNineSetups = "Loading setups...";
        state.errorMap.fetchNineSetups = null;
        state.successMap.fetchNineSetups = false;
      })
      .addCase(fetchNineSetups.fulfilled, (state, action) => {
        state.loadingMap.fetchNineSetups = null;
        state.successMap.fetchNineSetups = true;
        state.setups = action.payload;
        // Drop a selection that no longer exists in the refreshed list, so a
        // detail panel can't sit open on a row the server has since removed.
        if (
          state.selectedSetupId &&
          !action.payload.some((s) => s.setupId === state.selectedSetupId)
        ) {
          state.selectedSetupId = null;
        }
      })
      .addCase(fetchNineSetups.rejected, (state, action) => {
        state.loadingMap.fetchNineSetups = null;
        state.errorMap.fetchNineSetups = action.payload as string;
      });

    // fetchOperatorQueue / fetchApprovedQueue / fetchCuratorSetups — each replaces its own list
    // wholesale. Neither touches `setups`: four scopes, four arrays.
    builder
      .addCase(fetchOperatorQueue.pending, (state) => {
        state.loadingMap.fetchOperatorQueue = "Loading the operator queue...";
        state.errorMap.fetchOperatorQueue = null;
      })
      .addCase(fetchOperatorQueue.fulfilled, (state, action) => {
        state.loadingMap.fetchOperatorQueue = null;
        state.successMap.fetchOperatorQueue = true;
        state.operatorQueue = action.payload;
      })
      .addCase(fetchOperatorQueue.rejected, (state, action) => {
        state.loadingMap.fetchOperatorQueue = null;
        state.errorMap.fetchOperatorQueue = action.payload as string;
      });

    builder
      .addCase(fetchApprovedQueue.pending, (state) => {
        state.loadingMap.fetchApprovedQueue = "Loading the queue...";
        state.loadingMap.fetchCuratorQueue = "Loading the queue...";
        state.errorMap.fetchApprovedQueue = null;
        state.errorMap.fetchCuratorQueue = null;
      })
      .addCase(fetchApprovedQueue.fulfilled, (state, action) => {
        state.loadingMap.fetchApprovedQueue = null;
        state.loadingMap.fetchCuratorQueue = null;
        state.successMap.fetchApprovedQueue = true;
        state.successMap.fetchCuratorQueue = true;
        state.queue = action.payload;
      })
      .addCase(fetchApprovedQueue.rejected, (state, action) => {
        state.loadingMap.fetchApprovedQueue = null;
        state.loadingMap.fetchCuratorQueue = null;
        state.errorMap.fetchApprovedQueue = action.payload as string;
        state.errorMap.fetchCuratorQueue = action.payload as string;
      });

    builder
      .addCase(fetchCuratorSetups.pending, (state) => {
        state.loadingMap.fetchCuratorSetups = "Loading your tasks...";
        state.errorMap.fetchCuratorSetups = null;
      })
      .addCase(fetchCuratorSetups.fulfilled, (state, action) => {
        state.loadingMap.fetchCuratorSetups = null;
        state.successMap.fetchCuratorSetups = true;
        state.claimed = action.payload;
      })
      .addCase(fetchCuratorSetups.rejected, (state, action) => {
        state.loadingMap.fetchCuratorSetups = null;
        state.errorMap.fetchCuratorSetups = action.payload as string;
      });

    builder
      .addCase(fetchTeamSetups.pending, (state) => {
        state.loadingMap.fetchTeamSetups = "Loading your team's setups...";
        state.errorMap.fetchTeamSetups = null;
        state.successMap.fetchTeamSetups = false;
      })
      .addCase(fetchTeamSetups.fulfilled, (state, action) => {
        state.loadingMap.fetchTeamSetups = null;
        state.successMap.fetchTeamSetups = true;
        state.teamSetups = action.payload;
      })
      .addCase(fetchTeamSetups.rejected, (state, action) => {
        state.loadingMap.fetchTeamSetups = null;
        state.errorMap.fetchTeamSetups = action.payload as string;
      });

    // createNineSetup — NOT idempotent server-side (customerMobile is UNIQUE),
    // so the component must block resubmits on this loading key.
    builder
      .addCase(createNineSetup.pending, (state) => {
        state.loadingMap.createNineSetup = "Creating setup...";
        state.errorMap.createNineSetup = null;
        state.successMap.createNineSetup = false;
      })
      .addCase(createNineSetup.fulfilled, (state, action) => {
        state.loadingMap.createNineSetup = null;
        state.successMap.createNineSetup = true;
        upsertNineSetup(state.setups, action.payload);
      })
      .addCase(createNineSetup.rejected, (state, action) => {
        state.loadingMap.createNineSetup = null;
        state.errorMap.createNineSetup = action.payload as string;
      });

    // updateNineSetup — PER-ROW keys (see updateNineSetupKey).
    builder
      .addCase(updateNineSetup.pending, (state, action) => {
        const key = updateNineSetupKey(action.meta.arg.setupId);
        state.loadingMap[key] = "Saving setup...";
        state.errorMap[key] = null;
        state.successMap[key] = false;
      })
      .addCase(updateNineSetup.fulfilled, (state, action) => {
        const key = updateNineSetupKey(action.meta.arg.setupId);
        state.loadingMap[key] = null;
        state.successMap[key] = true;
        const row = action.payload;
        // Swap in the server's row wholesale rather than merging the local
        // edit: a status change also moves a lifecycle timestamp (and possibly
        // curatorId/businessId) that only the server knows about.
        upsertNineSetup(state.setups, row);

        // Keep the operator and curator lists coherent:
        // 1. Operator queue: approved setups waiting for media collection
        if (row.status === "approved") {
          upsertNineSetup(state.operatorQueue, row);
        } else {
          state.operatorQueue = state.operatorQueue.filter((s) => s.setupId !== row.setupId);
        }

        // 2. Curator queue: media_collected setups waiting for curators to claim
        if (row.status === "media_collected") {
          upsertNineSetup(state.queue, row);
        } else {
          state.queue = state.queue.filter((s) => s.setupId !== row.setupId);
        }

        // Only touch `claimed` for rows already there, or ones this update just
        // claimed. Every other status change belongs to a captain and must not
        // silently add work to a curator's list.
        const alreadyClaimed = state.claimed.some(
          (s) => s.setupId === row.setupId
        );
        if (alreadyClaimed || action.meta.arg.status === "claimed") {
          upsertNineSetup(state.claimed, row);
        }
      })
      .addCase(updateNineSetup.rejected, (state, action) => {
        const key = updateNineSetupKey(action.meta.arg.setupId);
        state.loadingMap[key] = null;
        state.errorMap[key] = action.payload as string;
      });

    // deleteNineSetup — PER-ROW keys, same rationale as update.
    builder
      .addCase(deleteNineSetup.pending, (state, action) => {
        const key = deleteNineSetupKey(action.meta.arg);
        state.loadingMap[key] = "Deleting setup...";
        state.errorMap[key] = null;
        state.successMap[key] = false;
      })
      .addCase(deleteNineSetup.fulfilled, (state, action) => {
        const deletedId = action.payload;
        const key = deleteNineSetupKey(action.meta.arg);
        state.loadingMap[key] = null;
        state.successMap[key] = true;
        state.setups = state.setups.filter((s) => s.setupId !== deletedId);
        if (state.selectedSetupId === deletedId) {
          state.selectedSetupId = null;
        }
      })
      .addCase(deleteNineSetup.rejected, (state, action) => {
        const key = deleteNineSetupKey(action.meta.arg);
        state.loadingMap[key] = null;
        state.errorMap[key] = action.payload as string;
      })
      // -- Calendar Data --
      .addCase(fetchCalendarData.pending, (state, action) => {
        state.loadingMap[fetchCalendarDataKey] = "Loading calendar data...";
        state.errorMap[fetchCalendarDataKey] = null;
        state.successMap[fetchCalendarDataKey] = false;
        state.calendarDataRequestId = action.meta.requestId;
      })
      .addCase(fetchCalendarData.fulfilled, (state, action) => {
        if (state.calendarDataRequestId === action.meta.requestId) {
          state.loadingMap[fetchCalendarDataKey] = null;
          state.successMap[fetchCalendarDataKey] = true;
          state.calendarData = action.payload;
        }
      })
      .addCase(fetchCalendarData.rejected, (state, action) => {
        if (state.calendarDataRequestId === action.meta.requestId) {
          state.loadingMap[fetchCalendarDataKey] = null;
          state.errorMap[fetchCalendarDataKey] = action.payload as string;
        }
      });

    // Onboarding Insights report range (all statuses), keyed by range.
    registerAsync(builder, fetchReportRange, {
      tab: (state) => state,
      key: ({ from, to }) => fetchReportRangeKey(reportRangeKey(from, to)),
      loading: "Loading report...",
      onFulfilled: (state, payload, { from, to }) => {
        state.reportRanges[reportRangeKey(from, to)] = payload;
      },
    });

    // Keep business names in sync across all setup lists when a business is updated
    builder.addCase(updateBusiness.fulfilled, (state, action) => {
      const updated = action.payload;
      const targetId = updated?.businessId;
      const newName = updated?.name;
      if (!targetId || !newName) return;

      const updateNameInList = (list: NineSetup[]) => {
        list.forEach((setup) => {
          if (setup.businessId === targetId) {
            setup.businessName = newName;
          }
        });
      };

      updateNameInList(state.claimed);
      updateNameInList(state.queue);
      updateNameInList(state.setups);
      updateNameInList(state.teamSetups);
    });

    builder.addCase(resetAllState, () => initialState);
  },
});

export const { selectNineSetup, clearNineSetupFeedback } =
  nineSetupSlice.actions;

export default nineSetupSlice.reducer;
